import axiosInstance from "../common/axiosInstance";
import { AxiosError } from "axios";
import {
  ApiRes,
  ApiResponse,
  areaRiskType,
  AssetsState,
  hospitalRiskType,
  initialChecks,
  maintenance,
  UserObject,
  Department,
  CreateCheckListForm,
  LawsAndRegulationsAudit,
  CreateAuditFormData,
  UpdateAuditFormData,
  AuditReminder,
  LawsAndRegulationsTask,
  CreateTaskFormData,
  UpdateTaskFormData,
  CreateTenantData,
  TenantResponse,
  WashroomTypeListType,
  WashroomTypeListResponse,
  ExternalAgency,
  AssetInventory,
  ContractorVendorManagement,
  UtilityAssetTypeListType,
  ComplianceCertificate,
  ComplianceChecklist,
  ComplianceChecklistConfig,
  SpaceUtilization,
  Project,
  ProjectApproval,
  ProjectApprovalLevel,
  ProjectPhase,
  InfectionControlChecklist,
  roundPlanningType,
} from "../types/api";

export * from "./domain/users";
export * from "./domain/tenants";
export * from "./domain/s3";
export * from "./domain/tasks";
export * from "./domain/premises";
export * from "./domain/assets";
export * from "./domain/incidents";
export * from "./domain/riskAssessment";
export * from "./domain/training";
export * from "./domain/budgeting";
export * from "./domain/compliance";
export * from "./domain/fireDrills";
export * from "./domain/policies";
export * from "./domain/washroom";
export * from "./domain/hazmat";
export * from "./domain/security";
export * from "./domain/ai";
export * from "./domain/rounds";
export * from "./domain/dashboard";

export interface ForgotPasswordRequest {
  email: string;
  tenant_name: string;
}

export interface ForgotPasswordResponse {
  success?: boolean;
  resCode?: number;
  resMessage?: string;
  message?: string;
  error?: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

export interface ValidateTokenRequest {
  token: string;
}

export interface ValidateTokenResponse {
  success?: boolean;
  message?: string;
  resMessage?: string;
  resCode?: number;
  data?: {
    email?: string;
    name?: string;
  };
}

// Interface for a Trainee  in a training session
export interface Trainee {
  traineeId: string;
  traineeEmployeeId: string;
  name: string;
  email?: string; // Optional, added for clarity
  department: string;
  role: string | { $oid: string }; // e.g., "Employee", "Contractor"
  roleName?: string; // Optional, used for orguser role
  attendance: {
    "Pre-Test": "Present" | "Absent";
    "Post-Test": "Present" | "Absent";
  };
  results?: {
    preTestId: string | null;
    preTestScore: number | null;
    postTestId: string | null;
    postTestScore: number | null;
  };
}

// Interface for a quiz question
export interface QuizQuestion {
  id: number; // Unique identifier for the question
  question: string;
  options: Array<{
    text: string;
    isCorrect: boolean;
  }>;
}

// Interface for a training session
export interface Training {
  id: string;
  topic: string;
  category: string;
  moduleType?: string;
  date: string;
  duration: string;
  instructor: {
    trainerId: string;
    trainerEmployeeId: string;
    trainerName: string;
  }[];
  method?: string;
  files: string[];
  trainees: Trainee[];
  status: "Active" | "Inactive";
  createdAt: string;
  updatedAt: string;
  tenantId: string;
  tenant_name?: string;
  tests?: Test[];
  createTest?: "Yes" | "No"; // Added by the backend in the "all" endpoint
}
// Interface for a test
export interface Test {
  id: string;
  trainingId: string;
  topic: string;
  testCategory: "Pre-Test" | "Post-Test";
  date: string;
  duration: string;
  quiz: {
    id: number;
    question: string;
    options: { text: string; isCorrect: boolean }[];
  }[];
  status: string;
  results?: {
    traineeId: string;
    score: number;
    submittedAt: string;
    answers: { questionId: number; selectedOption: number }[];
  }[];
  createdAt: string;
  updatedAt: string;
  tenantId: string;
}
interface folderType {
  createdAt: number;
  updatedAt: number;
  id: string;
  folderName: string;
  files: null | Array<{
    name: string;
    size: string;
    tags: string[];
    expiry: string;
    reminder: string;
    url: string;
    type: string;
    region: string;
    premises: string;
  }>;
  createdDate: string;
  uploadedBy: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface res {
  resCode: number | string;
  resMessage: string;
  message: object | object[];
}

interface I_UserParam {
  user: string;
  password: string;
}

interface EquipmentHistoryResponse<T> {
  resCode: number;
  resMessage: string;
  message: T;
}

export interface EquipmentHistory {
  _id?: string;
  id: string;
  eventId: string;
  equipmentId: string;
  equipmentName: string;
  eventType:
  | "Purchase"
  | "Service"
  | "Repair"
  | "Calibration"
  | "Transfer"
  | "Decommission"
  | "Inspection"
  | "Incident";
  eventDate: string;
  notes: string;
  cost?: number;
  attachments?: Attachment[];
  performedBy?: string;
  vendor?: string;
  vendorName?: string;
  region?: string;
  premises?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Attachment {
  url: string;
  filename?: string;
  name?: string;
  originalName?: string;
  uploadedAt?: string;
}

// Login and User-Related Endpoints
export const Login = (param: I_UserParam) => {
  return axiosInstance.post<ApiResponse<res>>("/api/login", param);
};

export const logOut = () => {
  return axiosInstance.post<ApiResponse<res>>("/api/logout");
};

// User Directory (for task assignments, document sharing, recipient selectors, etc.)
export const getUserDirectory = (param?: any) => {
  return axiosInstance.get<ApiResponse<UserObject[]>>("/api/orgUser/directory", {
    params: param,
  });
};

export const getUsers = (param?: UserObject) => {
  return axiosInstance.get<ApiResponse<UserObject[]>>("/api/orgUser/directory", {
    params: param,
  });
};

// Admin full user list endpoint
export const getAdminUsers = (param?: UserObject) => {
  return axiosInstance.get<ApiResponse<UserObject[]>>("/api/orgUser/user", {
    params: param,
  });
};

export const forgotPassword = (data: ForgotPasswordRequest) => {
  return axiosInstance.post<ApiResponse<ForgotPasswordResponse>>(
    "/api/tenant/forgot-password",
    data,
  );
};

export const resetPasswordWithToken = (data: ResetPasswordRequest) => {
  return axiosInstance.post<ApiResponse<ForgotPasswordResponse>>(
    "/api/tenant/reset-password-with-token",
    data,
  );
};

export const validateResetToken = (data: ValidateTokenRequest) => {
  return axiosInstance.post<ApiResponse<ValidateTokenResponse>>(
    "/api/tenant/validate-reset-token",
    data,
  );
};

// Policies and Manuals Endpoints
export const createFolders = (param: { details: { folderName: string }[] }) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/policiesmanuals/createfilefolder",
    param,
  );
};

export const allFolders = (params = {}) => {
  return axiosInstance.get<ApiRes<any>>("/api/policiesmanuals", { params });
};

export const getFiles = (
  id: string | undefined,
  params: { region?: string; premises?: string } = {},
) => {
  return axiosInstance.get<ApiRes<any>>(
    "/api/policiesmanuals/" + id + "/contents",
    { params },
  );
};

export const getFolderContents = (
  id: string | undefined,
  params: { region?: string; premises?: string } = {},
) => {
  return axiosInstance.get<ApiRes<any>>(`/api/policiesmanuals/${id}/contents`, {
    params,
  });
};

export const updatefilefolder = (params: any, id: string | undefined) => {
  return axiosInstance.put<ApiRes<any>>("/api/policiesmanuals/" + id, params);
};

export const deletefilefolder = (id: string | undefined) => {
  return axiosInstance.delete<ApiRes<any>>("/api/policiesmanuals/" + id);
};

export const filterPolicies = (params: {
  region?: string;
  premises?: string;
  keyword?: string;
  category?: string;
  expiryDate?: string;
  folderName?: string;
}) => {
  return axiosInstance.get<ApiRes<any>>("/api/policiesmanuals/filter", {
    params,
  });
};

export const getFolderTags = (folderId: string | undefined) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/policiesmanuals/${folderId}/tags`,
  );
};

export const updateFolderTags = (
  folderId: string | undefined,
  params: { tags: string[] },
) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/policiesmanuals/${folderId}/tags`,
    params,
  );
};

export const deleteFolderTags = (folderId: string | undefined) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/policiesmanuals/${folderId}/tags`,
  );
};

export const findOnePolicy = (id: string | undefined) => {
  return axiosInstance.get<ApiRes<any>>(`/api/policiesmanuals/${id}`);
};

export const searchPolicies = (params: {
  keyword?: string;
  [key: string]: any;
}) => {
  return axiosInstance.get<ApiRes<any>>("/api/policiesmanuals/search", {
    params,
  });
};

export const createTaskTrigger = (params: { [key: string]: any }) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/policiesmanuals/tasktrigger",
    params,
  );
};

export const addTags = (params: { folderId: string; newTags: string[] }) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/policiesmanuals/addtags",
    params,
  );
};

export const downloadFolder = (folderId: string | undefined) => {
  return axiosInstance.get(`/api/policiesmanuals/${folderId}/download`, {
    responseType: "blob", // Important for handling binary data
  });
};

// Compliance Audit & Documentation Endpoints
export const createComplianceAuditFolder = (param: {
  details: { folderName: string }[];
}) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/compliance-audit-documentation/createfilefolder",
    param,
  );
};

export const listComplianceAuditFolders = (params = {}) => {
  return axiosInstance.get<ApiRes<any>>(
    "/api/compliance-audit-documentation/list",
    { params },
  );
};

export const getComplianceAuditFolderContents = (
  id: string | undefined,
  params: {} = {},
) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/compliance-audit-documentation/${id}/contents`,
    { params },
  );
};

export const getComplianceAuditFolderDetails = (id: string | undefined) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/compliance-audit-documentation/${id}/details`,
  );
};

export const updateComplianceAuditFolder = (
  params: any,
  id: string | undefined,
) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/compliance-audit-documentation/${id}/update`,
    params,
  );
};

export const deleteComplianceAuditFolder = (id: string | undefined) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/compliance-audit-documentation/${id}/delete`,
  );
};

export const downloadComplianceAuditFolder = (folderId: string | undefined) => {
  return axiosInstance.get(
    `/api/compliance-audit-documentation/${folderId}/download`,
    {
      responseType: "blob", // Important for handling binary data
    },
  );
};

// Risk Assessment Endpoints
export const createRiskAssessment = (param: any, moduleName?: string) => {
  const payload = moduleName ? { ...param, moduleName } : param;
  return axiosInstance.post<ApiRes<any>>("api/riskAssessment", payload);
};


export const getRiskAssesments = async (
  moduleName?: string,
  regionId?: string,
  premiseId?: string
) => {
  try {
    const params = new URLSearchParams();

    if (moduleName) {
      params.append("moduleName", moduleName);
    }

    if (regionId) {
      params.append("regionId", regionId);
    }

    if (premiseId) {
      params.append("premiseId", premiseId);
    }

    const response = await axiosInstance.get(
      `/api/riskassessment/?${params.toString()}`
    );

    return response;
  } catch (error) {
    throw error;
  }
};



export const getRiskAssesmentById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>("api/riskassessment/" + id);
};

export const searchRiskAssessment = (keyword: string, moduleName?: string) => {
  const params = moduleName ? `?moduleName=${moduleName}` : "";
  return axiosInstance.get<ApiRes<any>>(
    `api/riskassessment/search/${keyword}${params}`,
  );
};

export const updateRiskAssessment = (
  id: string,
  data: any,
  moduleName?: string,
) => {
  const payload = moduleName ? { ...data, moduleName } : data;
  return axiosInstance.put<ApiRes<any>>("api/riskAssessment/" + id, payload);
};

export const taskRisk = (id: string) => {
  return axiosInstance.get<any>("api/task/risk/" + id);
};

export const deleteRiskAssessment = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>("api/riskassessment/" + id);
};

// Hospital-Wide Risk (HWR) Endpoints
export const getAllHwr = (id: string, moduleName?: string) => {
  const params = moduleName ? `?moduleName=${moduleName}` : "";
  return axiosInstance.get<ApiRes<any>>(
    "api/hospitalwiderisk/all/" + id + params,
  );
};

export const createhwr = (data: hospitalRiskType, moduleName?: string) => {
  const payload = moduleName ? { ...data, moduleName } : data;
  return axiosInstance.post<ApiRes<any>>("api/hospitalwiderisk", payload);
};

export const updateHwr = (
  id: string,
  data: hospitalRiskType,
  moduleName?: string,
) => {
  const payload = moduleName ? { ...data, moduleName } : data;
  return axiosInstance.put<ApiRes<any>>("api/hospitalwiderisk/" + id, payload);
};

export const fetchHWRScore = (id: string) => {
  return axiosInstance.get<ApiRes<any>>("api/hospitalwiderisk/score/" + id);
};

export const deleteHwr = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>("api/hospitalwiderisk/" + id);
};

// Area-Specific Risk (ASR) Endpoints
export const getAllASR = (id: string, moduleName?: string) => {
  const params = moduleName ? `?moduleName=${moduleName}` : "";
  return axiosInstance.get<ApiRes<any>>(
    "api/areaspecificrisk/all/" + id + params,
  );
};

export const createasr = (data: areaRiskType, moduleName?: string) => {
  const payload = moduleName ? { ...data, moduleName } : data;
  return axiosInstance.post<ApiRes<any>>("api/areaspecificrisk", payload);
};

export const updateASR = (
  id: string,
  data: hospitalRiskType,
  moduleName?: string,
) => {
  const payload = moduleName ? { ...data, moduleName } : data;
  return axiosInstance.put<ApiRes<any>>("api/areaspecificrisk/" + id, payload);
};

export const fetchASRScore = (id: string) => {
  return axiosInstance.get<ApiRes<any>>("api/areaspecificrisk/score/" + id);
};

export const deleteASR = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>("api/areaspecificrisk/" + id);
};

// Risk Assessment Score Endpoint
export const getAssessmentScore = (id: string) => {
  return axiosInstance.get<ApiRes<any>>("api/riskassessment/score/" + id);
};

// Fire Safety and Equipment Endpoints
export const createEquipment = (param: any) => {
  return axiosInstance.post<ApiRes<any>>("api/equipment", param);
};

// Equipment Management Module API Endpoints
export const createEquipmentManagement = (param: any) => {
  return axiosInstance.post<ApiRes<any>>("api/equipment-management", param);
};
export const fetchAllById = (_id: string) => {
  return axiosInstance.get<ApiRes<any>>(`api/equipment-management/all/${_id}`);
};

export const getAllEquipmentManagement = () => {
  return axiosInstance.get<ApiRes<any>>("api/equipment-management");
};

export const getAllEquipmentManagementData = () => {
  return axiosInstance.get<ApiRes<any>>("api/equipment-management/all");
};

export const getEquipmentManagementById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`api/equipment-management/${id}`);
};

export const updateEquipmentManagement = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(`api/equipment-management/${id}`, data);
};

export const deleteEquipmentManagement = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(`api/equipment-management/${id}`);
};

// ---------- Equipment Management Task Module Endpoints
export const getAllEquipmentManagementTask = () => {
  return axiosInstance.get<ApiRes<any>>("api/equipment-management-task");
};

export const updateEquipmentManagementTask = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(
    `api/equipment-management-task/${id}`,
    data,
  );
};

export const getEquipmentManagementByExpirationRange = (params: any) => {
  return axiosInstance.get<ApiRes<any>>(
    "api/equipment-management/expiration-range",
    { params },
  );
};

export const getExpiringSoonEquipment = () => {
  return axiosInstance.get<ApiRes<any>>(
    "api/equipment-management/expiring-soon",
  );
};

// ========== Resource Readiness Module API Endpoints ==========
export const createResourceReadiness = (param: any) => {
  return axiosInstance.post<ApiRes<any>>("api/resource-readiness", param);
};

export const getAllResourceReadiness = () => {
  return axiosInstance.get<ApiRes<any>>("api/resource-readiness/all");
};

export const getResourceReadinessById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`api/resource-readiness/${id}`);
};

export const updateResourceReadiness = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(`api/resource-readiness/${id}`, data);
};

export const deleteResourceReadiness = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(`api/resource-readiness/${id}`);
};

export const getResourceReadinessByInspectionDue = (days?: number) => {
  return axiosInstance.get<ApiRes<any>>(
    "api/resource-readiness/inspection-due",
    {
      params: { days },
    },
  );
};

export const getResourceReadinessByStatus = (status: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `api/resource-readiness/status/${status}`,
  );
};

// ========== Readiness Checklist Template Endpoints ==========
export const getAllReadinessTemplates = () => {
  return axiosInstance.get<ApiRes<any>>(
    "api/external-preparedness/readiness/templates",
  );
};

export const getReadinessTemplateById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `api/external-preparedness/readiness/templates/${id}`,
  );
};

export const createReadinessTemplate = (data: any) => {
  return axiosInstance.post<ApiRes<any>>(
    "api/external-preparedness/readiness/templates",
    data,
  );
};

export const updateReadinessTemplate = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(
    `api/external-preparedness/readiness/templates/${id}`,
    data,
  );
};

export const deleteReadinessTemplate = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `api/external-preparedness/readiness/templates/${id}`,
  );
};

// ========== Readiness Inspection Endpoints ==========
export const createReadinessInspection = (data: any) => {
  return axiosInstance.post<ApiRes<any>>(
    "api/external-preparedness/readiness/inspect",
    data,
  );
};

export const getReadinessInspectionHistory = (equipmentId: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `api/external-preparedness/readiness/inspect/history/${equipmentId}`,
  );
};

export const getReadinessInspectionById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `api/external-preparedness/readiness/inspect/${id}`,
  );
};

// Checks Endpoints
export const createChecks = (param: initialChecks) => {
  return axiosInstance.post<ApiRes<any>>("api/checks", param);
};

export const getAllChecks = (id: string) => {
  return axiosInstance.get<ApiRes<any>>("api/checks/" + id);
};

export const deleteChecks = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>("api/checks/" + id);
};

export const updateChecks = (id: string, data: initialChecks) => {
  return axiosInstance.put<ApiRes<any>>(`api/checks/${id}`, data);
};

// Assets Endpoints

// ++ create task on every checks
/* export const createTask = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`api/task/firesafetytask/${id}`);
}; */
// ++ create assets

export const createAssets = (param: any) => {
  return axiosInstance.post<ApiRes<any>>("api/assets", param);
};

export const getAllAssets = () => {
  return axiosInstance.get<ApiRes<any>>("api/assets");
};

export interface PaginatedAssetsParams {
  page?: number;
  limit?: number;
  search?: string;
  equipmentInventory?: string;
  floor?: string;
  premisesName?: string;
  premisesId?: string;
  region?: string;
  sort?: string;
}

export const getPaginatedAssets = (params: PaginatedAssetsParams) => {
  return axiosInstance.get<any>("api/assets", { params });
};

export const updateAssets = (id: string, data: AssetsState) => {
  return axiosInstance.put<ApiRes<any>>("api/assets/" + id, data);
};

export const searchAssets = (keyword: string) => {
  return axiosInstance.get<ApiRes<any>>(`api/assets/search/${keyword}`);
};

export const filterAssetsByFloor = (floor: string) => {
  return axiosInstance.get<ApiRes<any>>(`api/assets/filter/${floor}`);
};

export const getOneAssets = (id: string) => {
  return axiosInstance.get<ApiRes<any>>("api/equipment/" + id);
};

//..... Training Endpoints started.......
export const createTraining = (param: Partial<Training>) => {
  return axiosInstance.post<ApiRes<{ success: boolean; data: Training }>>(
    "/api/training",
    param,
  );
};

export const updateTraining = (id: string, data: Partial<Training>) => {
  return axiosInstance.put<ApiRes<{ success: boolean; data: Training }>>(
    `/api/training/${id}`,
    data,
  );
};

export const getTrainingById = (id: string) => {
  return axiosInstance.get<ApiRes<{ success: boolean } & Training>>(
    `/api/training/${id}`,
  );
};

// export const searchTrainings = (keyword: string) => {
//   return axiosInstance.get<ApiRes<Training[]>>(
//     `/api/training/search/${keyword}`
//   );
// };

export const getAllTrainings = (
  params: {
    type?: "upcoming" | "past" | "all";
    limit?: number;
    skip?: number;
    sort?: string;
    where?: object;
    /** Comma-separated region ids. Empty/missing training.regions matches any. */
    region?: string;
    /** Comma-separated premises ids. Empty/missing training.premises matches any. */
    premises?: string;
    /** Overrides type's binary upcoming/past bucket with an explicit range. */
    startDate?: string;
    endDate?: string;
    /** Scopes an orguser's results to just one side instead of the combined default. */
    roleFilter?: "trainee" | "trainer";
    /** Returns a `stats` block (total/upcoming/past/pending counts) alongside results. */
    includeStats?: boolean;
  } = {},
) => {
  return axiosInstance.get<
    ApiRes<{
      success: boolean;
      totalCount: number;
      results: Training[];
      stats?: {
        totalTrainings: number;
        upcomingSessionsCount: number;
        pastTrainingsCount: number;
        pendingSessionsCount: number;
      };
    }>
  >("/api/training/all", { params });
};

export const filterTrainings = (params: { region: string }) => {
  return axiosInstance.post<
    ApiRes<{ success: boolean; message: string; filterTraining: Training[] }>
  >("/api/training/filter", params);
};

export const toggleTrainingStatus = (id: string) => {
  return axiosInstance.put<ApiRes<{ success: boolean; data: Training }>>(
    `/api/training/${id}/toggle-status`,
  );
};

export const deleteTraining = (id: string) => {
  return axiosInstance.delete<ApiRes<{ success: boolean }>>(
    `/api/training/${id}`,
  );
};

// api.ts
export const updateAttendance = async (
  id: string,
  updates: {
    traineeId: string;
    attendance: "Present" | "Absent";
    testCategory: "Pre-Test" | "Post-Test";
  }[],
  tenantId: string, // 👈 include this parameter
) => {
  const response = await axiosInstance.put<
    ApiRes<{ success: boolean; data: Training }>
  >(`/api/training/${id}/update-attendance`, {
    updates,
    tenantId, // 👈 send this
  });
  return response;
};

// Test Endpoints
export const createTest = (param: Partial<Test>) => {
  return axiosInstance.post<ApiRes<{ success: boolean; data: Test }>>(
    "/api/test",
    param,
  );
};

export const updateTest = (id: string, data: Partial<Test>) => {
  return axiosInstance.put<ApiRes<{ success: boolean; data: Test }>>(
    `/api/test/${id}`,
    data,
  );
};

export const deleteTest = (id: string) => {
  return axiosInstance.delete<ApiRes<{ success: boolean }>>(
    `/api/test/${id}`,
  );
};

// export const getTestById = (id: string) => {
//   return axiosInstance.get<ApiRes<{ success: boolean } & Test>>(
//     `/api/test/${id}`
//   );
// };

export const submitTest = async (
  id: string,
  answers: { questionId: number; selectedOption: number }[],
  traineeId: string,
): Promise<{ score: number; message: string }> => {
  try {
    const response = await axiosInstance.post<
      ApiRes<{ score: number; message: string }>
    >(`/api/test/${id}/submit`, { answers, traineeId });
    // if (!response.data.success) {
    //   throw new Error(
    //     typeof response.data.resMessage === "string"
    //       ? response.data.resMessage
    //       : "Failed to submit test"
    //   );
    // }
    if (!response.data) {
      throw new Error("No data returned from test submission");
    }
    // if (!response.data.data) {
    //   throw new Error("No data returned from test submission");
    // }
    return response.data.data as { score: number; message: string }; // TypeScript should now recognize this as { score: number; message: string }
  } catch (error) {
    const err = error as AxiosError<{ resMessage?: string }>;
    console.error("API error:", err.response?.data || err.message);
    throw new Error(
      err.response?.data?.resMessage || err.message || "Failed to submit test",
    );
  }
};

export const getTestByTrainingIdApi = (trainingId: string) => {
  return axiosInstance.get<ApiRes<{ success: boolean; results: Test[] }>>(
    `/api/test/training/${trainingId}`,
  );
};
export const getAllTests = () => {
  return axiosInstance.get<ApiRes<{ success: boolean; results: Test[] }>>(
    "/api/test",
  );
};
// export const getTestsByTrainerApi = () => {
//   return axiosInstance.get<ApiRes<{ success: boolean; results: Test[] }>>(
//     `/api/test/all`
//   );
// };

// Department Endpoints
export const getDepartments = (ids: string[]) => {
  return axiosInstance.get<
    ApiRes<{ data: { _id: string; departmentName: string }[] }>
  >("/api/departments");
};
//......training endponits closed.....

// ++ filter by region
export const filterAssetsByRegion = (regionId: string) => {
  return axiosInstance.get<ApiRes<any>>("api/assets/filter/region/" + regionId);
};
// ++ filter by premises
export const filterAssetsByPremises = (premiseId: string) => {
  return axiosInstance.get<ApiRes<any>>(
    "api/assets/filter/premises/" + premiseId,
  );
};
// ++ creating maintenance
export const createMaintenance = (param: maintenance) => {
  return axiosInstance.post<ApiRes<any>>("api/maintenance", param);
};
// ++ getAll maintenance
export const getAllMaintenance = (id: string) => {
  return axiosInstance.get<ApiRes<any>>("api/maintenance/" + id);
};
// ++ get one maintenance
export const getOneMaintenance = (id: string) => {
  return axiosInstance.get<ApiRes<any>>("api/maintenance/one/" + id);
};

// Maintenance Schedule APIs
export const createMaintenanceSchedule = (data: any) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/preventive-maintenance/schedules",
    data,
  );
};

export const getAllMaintenanceSchedules = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>(
    "/api/preventive-maintenance/schedules",
    { params },
  );
};

export const getMaintenanceSchedule = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/preventive-maintenance/schedules/${id}`,
  );
};

export const updateMaintenanceSchedule = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/preventive-maintenance/schedules/${id}`,
    data,
  );
};

export const deleteMaintenanceSchedule = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/preventive-maintenance/schedules/${id}`,
  );
};

export const searchMaintenanceSchedules = (keyword: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/preventive-maintenance/schedules/search/${keyword}`,
  );
};

// Preventive Maintenance Task APIs
export const createPreventiveMaintenanceTask = (data: any) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/preventive-maintenance/tasks",
    data,
  );
};

export const getAllPreventiveMaintenanceTasks = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/preventive-maintenance/tasks", {
    params,
  });
};

export const getPreventiveMaintenanceTask = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/preventive-maintenance/tasks/${id}`,
  );
};

export const updatePreventiveMaintenanceTask = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/preventive-maintenance/tasks/${id}`,
    data,
  );
};

export const deletePreventiveMaintenanceTask = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/preventive-maintenance/tasks/${id}`,
  );
};

export const completePreventiveMaintenanceTask = (id: string, data?: any) => {
  return axiosInstance.post<ApiRes<any>>(
    `/api/preventive-maintenance/tasks/${id}/complete`,
    data,
  );
};

export const getTasksByTechnician = (technicianId: string, params?: any) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/preventive-maintenance/tasks/technician/${technicianId}`,
    { params },
  );
};

// Maintenance Log APIs
export const createMaintenanceLog = (data: any) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/preventive-maintenance/logs",
    data,
  );
};

export const getAllMaintenanceLogs = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/preventive-maintenance/logs", {
    params,
  });
};

export const getMaintenanceLog = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/preventive-maintenance/logs/${id}`,
  );
};

export const updateMaintenanceLog = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/preventive-maintenance/logs/${id}`,
    data,
  );
};

export const deleteMaintenanceLog = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/preventive-maintenance/logs/${id}`,
  );
};

export const getLogsByAsset = (assetId: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/preventive-maintenance/logs/asset/${assetId}`,
  );
};

export const getLogsByTask = (taskId: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/preventive-maintenance/logs/task/${taskId}`,
  );
};

// ========== MONITORING & IOT INTEGRATION APIs ==========

// IoT Reading APIs
export const ingestIoTReadings = (data: any | any[]) => {
  return axiosInstance.post<ApiRes<any>>("/api/iot/readings/ingest", data);
};

export const createIoTReading = (data: any) => {
  return axiosInstance.post<ApiRes<any>>("/api/iot/readings", data);
};

export const getAllIoTReadings = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/iot/readings", { params });
};

export const getIoTReadingsByAsset = (
  assetId: string,
  params?: { hours?: number; sensorType?: string },
) => {
  return axiosInstance.get<ApiRes<any>>(`/api/iot/readings/asset/${assetId}`, {
    params,
  });
};

export const getIoTReadingsBySensor = (
  sensorId: string,
  params?: { hours?: number },
) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/iot/readings/sensor/${sensorId}`,
    { params },
  );
};

export const getLatestIoTReading = (assetId: string, sensorType: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/iot/readings/latest/${assetId}/${sensorType}`,
  );
};

// IoT Threshold APIs
export const createIoTThreshold = (data: any) => {
  return axiosInstance.post<ApiRes<any>>("/api/iot/thresholds", data);
};

export const getAllIoTThresholds = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/iot/thresholds", { params });
};

export const getIoTThreshold = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/iot/thresholds/${id}`);
};

export const updateIoTThreshold = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(`/api/iot/thresholds/${id}`, data);
};

export const deleteIoTThreshold = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(`/api/iot/thresholds/${id}`);
};

// IoT Alert APIs
export const getAllIoTAlerts = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/iot/alerts", { params });
};

export const getIoTAlert = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/iot/alerts/${id}`);
};

export const acknowledgeIoTAlert = (id: string) => {
  return axiosInstance.put<ApiRes<any>>(`/api/iot/alerts/${id}/acknowledge`);
};

export const resolveIoTAlert = (id: string) => {
  return axiosInstance.put<ApiRes<any>>(`/api/iot/alerts/${id}/resolve`);
};

export const dismissIoTAlert = (id: string) => {
  return axiosInstance.put<ApiRes<any>>(`/api/iot/alerts/${id}/dismiss`);
};

export const getActiveIoTAlertsCount = () => {
  return axiosInstance.get<ApiRes<any>>("/api/iot/alerts/count/active");
};

export const generateDummyIoTData = (params?: {
  assetId?: string;
  count?: number;
}) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/iot/readings/generate-dummy",
    params || {},
  );
};

export const createProject = (data: Partial<Project>) => {
  return axiosInstance.post<ApiRes<Project>>(
    "/api/project-planning/projects",
    data,
  );
};

export const getAllProjects = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/project-planning/projects", {
    params,
  });
};

export const getProject = (id: string) => {
  return axiosInstance.get<ApiRes<Project>>(
    `/api/project-planning/projects/${id}`,
  );
};

export const updateProject = (id: string, data: Partial<Project>) => {
  return axiosInstance.put<ApiRes<Project>>(
    `/api/project-planning/projects/${id}`,
    data,
  );
};

export const deleteProject = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/project-planning/projects/${id}`,
  );
};

export const submitProjectForApproval = (
  id: string,
  approvalLevels: ProjectApprovalLevel[],
) => {
  return axiosInstance.post<ApiRes<Project>>(
    `/api/project-planning/projects/${id}/submit`,
    {
      approvalLevels,
    },
  );
};

// ========== BUDGETING & COST CONTROL ==========
export const createProjectPhase = (data: Partial<ProjectPhase>) => {
  return axiosInstance.post<ApiRes<ProjectPhase>>(
    "/api/budgeting-cost-control/phases",
    data,
  );
};

export const getAllProjectPhases = (params?: any) => {
  return axiosInstance.get<ApiRes<ProjectPhase[]>>(
    "/api/budgeting-cost-control/phases",
    { params },
  );
};

export const getProjectPhase = (id: string) => {
  return axiosInstance.get<ApiRes<ProjectPhase>>(
    `/api/budgeting-cost-control/phases/${id}`,
  );
};

export const updateProjectPhase = (id: string, data: Partial<ProjectPhase>) => {
  return axiosInstance.put<ApiRes<ProjectPhase>>(
    `/api/budgeting-cost-control/phases/${id}`,
    data,
  );
};

export const deleteProjectPhase = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/budgeting-cost-control/phases/${id}`,
  );
};

export const getProjectPhasesByProject = (projectId: string) => {
  return axiosInstance.get<ApiRes<ProjectPhase[]>>(
    `/api/budgeting-cost-control/phases/project/${projectId}`,
  );
};

export const getProjectApprovals = (projectId: string) => {
  return axiosInstance.get<ApiRes<ProjectApproval>>(
    "/api/project-planning/approvals",
    {
      params: { projectId },
    },
  );
};

export const getProjectApproval = (id: string) => {
  return axiosInstance.get<ApiRes<ProjectApproval>>(
    `/api/project-planning/approvals/${id}`,
  );
};

export const approveProject = (
  approvalId: string,
  level: number,
  remarks?: string,
) => {
  return axiosInstance.post<ApiRes<ProjectApproval>>(
    `/api/project-planning/approvals/${approvalId}/approve`,
    {
      level,
      remarks,
    },
  );
};

export const rejectProject = (
  approvalId: string,
  level: number,
  remarks: string,
) => {
  return axiosInstance.post<ApiRes<ProjectApproval>>(
    `/api/project-planning/approvals/${approvalId}/reject`,
    {
      level,
      remarks,
    },
  );
};

export const createInfectionControlChecklist = (
  data: Partial<InfectionControlChecklist>,
) => {
  return axiosInstance.post<ApiRes<InfectionControlChecklist>>(
    "/api/infection-control/checklists",
    data,
  );
};

export const getAllInfectionControlChecklists = (params?: any) => {
  return axiosInstance.get<ApiRes<InfectionControlChecklist[]>>(
    "/api/infection-control/checklists",
    { params },
  );
};

export const getInfectionControlChecklist = (id: string) => {
  return axiosInstance.get<ApiRes<InfectionControlChecklist>>(
    `/api/infection-control/checklists/${id}`,
  );
};

export const updateInfectionControlChecklist = (
  id: string,
  data: Partial<InfectionControlChecklist>,
) => {
  return axiosInstance.put<ApiRes<InfectionControlChecklist>>(
    `/api/infection-control/checklists/${id}`,
    data,
  );
};

export const deleteInfectionControlChecklist = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/infection-control/checklists/${id}`,
  );
};

export const approveInfectionControlChecklist = (
  id: string,
  remarks?: string,
) => {
  return axiosInstance.post<ApiRes<InfectionControlChecklist>>(
    `/api/infection-control/checklists/${id}/approve`,
    { remarks },
  );
};

export const getInfectionControlChecklistsByProject = (projectId: string) => {
  return axiosInstance.get<ApiRes<InfectionControlChecklist[]>>(
    `/api/infection-control/checklists/project/${projectId}`,
  );
};

// fire safety audits
export const getAllAuditsHandler = (moduleName: string) => {
  const params = moduleName ? `?moduleName=${moduleName}` : "";
  return axiosInstance.get<ApiRes<any>>(`api/audits/${params}`);
};
export const createCheck = (params: CreateCheckListForm) => {
  return axiosInstance.post<ApiRes<any>>("api/checklist", params);
};
export const getAllCheck = () => {
  return axiosInstance.get<ApiRes<any>>("api/checklist");
};
export const deleteCheck = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>("api/checklist/" + id);
};
export const getCheck = (id: string) => {
  return axiosInstance.get<ApiRes<any>>("api/checklist/" + id);
};
export const searchCheck = (keyword: string) => {
  return axiosInstance.get<ApiRes<any>>("api/checklist/search/" + keyword);
};

// User Training Analytics API functions
export const getUserTrainingAnalytics = async (
  userId: string,
  scope?: { moduleType?: string; category?: string },
) => {
  try {
    const response = await axiosInstance.get(
      `/api/user-training-analytics/${userId}`,
      { params: scope },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUserTrainingAnalytics = async (
  userId: string,
  data: any,
) => {
  try {
    const response = await axiosInstance.put(
      `/api/user-training-analytics/${userId}/update`,
      data,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// External Agencies Endpoints
export const createExternalAgency = (param: Partial<ExternalAgency>) => {
  return axiosInstance.post<
    ApiRes<{ success: boolean; agency: ExternalAgency }>
  >("/api/external-agencies", param);
};

export const exportExternalAgencies = () => {
  return axiosInstance.get("/api/external-agencies/export", {
    responseType: "blob",
  });
};

export const getAllExternalAgencies = () => {
  return axiosInstance.get<ApiRes<ExternalAgency[]>>("/api/external-agencies");
};

export const updateExternalAgency = (
  id: string,
  data: Partial<ExternalAgency>,
) => {
  return axiosInstance.put<ApiRes<{ message: string; updateResult: any }>>(
    `/api/external-agencies/${id}`,
    data,
  );
};

export const deleteExternalAgency = (id: string) => {
  return axiosInstance.delete<ApiRes<{ message: string; deletedId: string }>>(
    `/api/external-agencies/${id}`,
  );
};

export const searchExternalAgencies = (keyword: string) => {
  return axiosInstance.get<ApiRes<ExternalAgency[]>>(
    `/api/external-agencies/search`,
    {
      params: { keyword },
    },
  );
};

export const getCategoryPerformance = async (userId: string) => {
  try {
    const response = await axiosInstance.get(
      `/api/user-training-analytics/${userId}/category-performance`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getMonthlyProgress = async (userId: string, year?: string) => {
  try {
    const url = year
      ? `/api/user-training-analytics/${userId}/monthly-progress/${year}`
      : `/api/user-training-analytics/${userId}/monthly-progress`;
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const syncAllUserTrainingAnalytics = async () => {
  try {
    const response = await axiosInstance.post(
      `/api/user-training-analytics/sync-all`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const syncUserScores = async (userId: string) => {
  try {
    const response = await axiosInstance.post(
      `/api/user-training-analytics/${userId}/sync-scores`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Premises API functions
export const getAllPremises = (params = {}) => {
  return axiosInstance.get("/api/premises", { params });
};

export const getPremiseById = (id: string) => {
  return axiosInstance.get(`/api/premises/${id}`);
};

export const createPremise = (data: any) => {
  return axiosInstance.post("/api/premises", data);
};

export const updatePremise = (id: string, data: any) => {
  return axiosInstance.put(`/api/premises/${id}`, data).catch((error) => {
    console.error(
      "Error updating premise:",
      error.response?.data || error.message,
    );
    throw error;
  });
};

export const deletePremise = (id: string) => {
  return axiosInstance.delete(`/api/premises/${id}`);
};

export const searchPremises = (keyword: string) => {
  return axiosInstance.get(`/api/premises/search/${keyword}`);
};

export const filterPremisesByRegion = (regionId: string) => {
  return axiosInstance.get(`/api/premises/filter/region/${regionId}`);
};

export const filterPremisesByPremise = (premiseId: string) => {
  return axiosInstance.get(`/api/premises/filter/premises/${premiseId}`);
};

export const togglePremiseStatus = (id: string, status: boolean) => {
  return axiosInstance.put(`/api/premises/toggle-status/${id}`, {
    isStatus: status,
  });
};

export const addIndependentUnit = (premiseId: string, unitData: any) => {
  return axiosInstance
    .post(`/api/premises/${premiseId}/independent-units`, unitData)
    .catch((error) => {
      console.error(
        "Error adding independent unit:",
        error.response?.data || error.message,
      );
      throw error;
    });
};

export const updateIndependentUnit = (
  premiseId: string,
  unitId: string,
  unitData: any,
) => {
  return axiosInstance.put(
    `/api/premises/${premiseId}/independent-units/${unitId}`,
    unitData,
  );
};

export const deleteIndependentUnit = (premiseId: string, unitId: string) => {
  return axiosInstance.delete(
    `/api/premises/${premiseId}/independent-units/${unitId}`,
  );
};

export const addAdditionalBuilding = (premiseId: string, buildingData: any) => {
  return axiosInstance.post(
    `/api/premises/${premiseId}/additional-buildings`,
    buildingData,
  );
};

export const updateAdditionalBuilding = (
  premiseId: string,
  buildingId: string,
  buildingData: any,
) => {
  return axiosInstance.put(
    `/api/premises/${premiseId}/additional-buildings/${buildingId}`,
    buildingData,
  );
};

export const deleteAdditionalBuilding = (
  premiseId: string,
  buildingId: string,
) => {
  return axiosInstance.delete(
    `/api/premises/${premiseId}/additional-buildings/${buildingId}`,
  );
};

// Staff of Independent Units API functions
export const getAllStaffIndependentUnits = (params = {}) => {
  return axiosInstance.get("/api/staff-independent-units", { params });
};

export const getStaffIndependentUnitById = (id: string) => {
  return axiosInstance.get(`/api/staff-independent-units/${id}`);
};

export const createStaffIndependentUnit = (data: any) => {
  return axiosInstance.post("/api/staff-independent-units", data);
};

export const updateStaffIndependentUnit = (id: string, data: any) => {
  return axiosInstance.put(`/api/staff-independent-units/${id}`, data);
};

export const deleteStaffIndependentUnit = (id: string) => {
  return axiosInstance.delete(`/api/staff-independent-units/${id}`);
};

// Temporarily commented out training-related functions for irregular staff
/*
export const addStaffTraining = (staffId: string, trainingData: any) => {
  return axiosInstance.post(`/api/staff-independent-units/${staffId}/training`, trainingData);
};

export const deleteStaffTraining = (staffId: string, trainingId: string) => {
  return axiosInstance.delete(`/api/staff-independent-units/${staffId}/training/${trainingId}`);
};

export const getStaffTrainingAlerts = () => {
  return axiosInstance.get("/api/staff-independent-units/training-alerts");
};
*/

// PolicyTags API functions for centralized tag management
export const getAllPolicyTags = (params = {}) => {
  return axiosInstance.get("/api/policytags/all", { params });
};

export const createPolicyTag = (data: {
  policytagID: string;
  policytagName: string;
}) => {
  return axiosInstance.post("/api/policytags", data);
};

export const updatePolicyTag = (
  id: string,
  data: { policytagID: string; policytagName: string },
) => {
  return axiosInstance.put(`/api/policytags/${id}`, data);
};

export const deletePolicyTag = (id: string) => {
  return axiosInstance.delete(`/api/policytags/${id}`);
};

export const createPolicyTagIfNotExists = (data: {
  policytagID: string;
  policytagName: string;
}) => {
  return axiosInstance.post("/api/policytags/createIfNotExists", data);
};

// Enhanced tag management functions for folders
export const getFolderTagsEnhanced = (folderId: string) => {
  return axiosInstance.get(`/api/policiesmanuals/${folderId}/tags`);
};

export const addTagsToFolder = (folderId: string, newTags: string[]) => {
  return axiosInstance.post("/api/policiesmanuals/addtags", {
    folderId,
    newTags,
  });
};

export const updateFolderTagsEnhanced = (
  folderId: string,
  updatedTags: string[],
) => {
  return axiosInstance.put(`/api/policiesmanuals/${folderId}/tags`, {
    updatedTags,
  });
};

export const deleteTagsFromFolder = (
  folderId: string,
  tagsToDelete: string[],
) => {
  return axiosInstance.delete(`/api/policiesmanuals/${folderId}/tags`, {
    data: { tagsToDelete },
  });
};

// Laws and Regulations Tasks API functions
export const getAllLawsRegulationsTasks = (params = {}) => {
  return axiosInstance.get<ApiRes<LawsAndRegulationsTask[]>>(
    "/api/laws-regulations-tasks",
    { params },
  );
};

export const getLawsRegulationsTaskById = (id: string) => {
  return axiosInstance.get<ApiRes<LawsAndRegulationsTask>>(
    `/api/laws-regulations-tasks/${id}`,
  );
};

export const createLawsRegulationsTask = (data: CreateTaskFormData) => {
  return axiosInstance.post<ApiRes<LawsAndRegulationsTask>>(
    "/api/laws-regulations-tasks",
    data,
  );
};

export const updateLawsRegulationsTask = (
  id: string,
  data: UpdateTaskFormData,
) => {
  return axiosInstance.put<ApiRes<LawsAndRegulationsTask>>(
    `/api/laws-regulations-tasks/${id}`,
    data,
  );
};

export const deleteLawsRegulationsTask = (id: string) => {
  return axiosInstance.delete<
    ApiRes<{ success: boolean; message: string; deletedTaskId: string }>
  >(`/api/laws-regulations-tasks/${id}`);
};

export const searchLawsRegulationsTasks = (keyword: string) => {
  return axiosInstance.get<ApiRes<LawsAndRegulationsTask[]>>(
    `/api/laws-regulations-tasks/search/${keyword}`,
  );
};

export const filterLawsRegulationsTasks = (filters: any) => {
  return axiosInstance.post<ApiRes<LawsAndRegulationsTask[]>>(
    "/api/laws-regulations-tasks/filter",
    filters,
  );
};

export const filterLawsRegulationsTasksByRegion = (regionName: string) => {
  return axiosInstance.get<ApiRes<LawsAndRegulationsTask[]>>(
    `/api/laws-regulations-tasks/filter/region/${regionName}`,
  );
};

export const completeLawsRegulationsTask = (id: string) => {
  return axiosInstance.post<ApiRes<LawsAndRegulationsTask>>(
    `/api/laws-regulations-tasks/${id}/complete`,
  );
};

// Laws and Regulations Audits API functions
export const getAllLawsRegulationsAudits = (params = {}) => {
  return axiosInstance.get<ApiRes<LawsAndRegulationsAudit[]>>(
    "/api/audits/external",
    { params },
  );
};

export const getLawsRegulationsAuditById = (id: string) => {
  return axiosInstance.get<ApiRes<LawsAndRegulationsAudit>>(
    `/api/audits/external/${id}`,
  );
};

export const createLawsRegulationsAudit = (data: CreateAuditFormData) => {
  return axiosInstance.post<ApiRes<LawsAndRegulationsAudit>>(
    "/api/audits/external",
    data,
  );
};

export const updateLawsRegulationsAudit = (
  id: string,
  data: UpdateAuditFormData,
) => {
  return axiosInstance.put<ApiRes<LawsAndRegulationsAudit>>(
    `/api/audits/external/${id}`,
    data,
  );
};

export const deleteLawsRegulationsAudit = (id: string) => {
  return axiosInstance.delete<ApiRes<LawsAndRegulationsAudit>>(
    `/api/audits/external/${id}`,
  );
};

export const searchLawsRegulationsAudits = (keyword: string) => {
  return axiosInstance.get<ApiRes<LawsAndRegulationsAudit[]>>(
    `/api/audits/external/search/${keyword}`,
  );
};

export const uploadAuditReportFile = (id: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return axiosInstance.post<ApiRes<LawsAndRegulationsAudit>>(
    `/api/audits/external/${id}/report`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const uploadAuditClosureFile = (id: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return axiosInstance.post<ApiRes<LawsAndRegulationsAudit>>(
    `/api/audits/external/${id}/closure`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const getAuditReminders = () => {
  return axiosInstance.get<ApiRes<AuditReminder[]>>(
    "/api/audits/external/reminders",
  );
};

// Laws and Regulations Dashboard API functions
export const getLawsRegulationsOverview = () => {
  return axiosInstance.get("/api/laws-regulations-dashboard/overview");
};

export const getLawsRegulationsAlerts = () => {
  return axiosInstance.get("/api/laws-regulations-dashboard/alerts");
};

export const getLawsRegulationsModuleStats = (module: string) => {
  return axiosInstance.get(`/api/laws-regulations-dashboard/stats/${module}`);
};

export const getLawsRegulationsRecentActivity = (params = {}) => {
  return axiosInstance.get("/api/laws-regulations-dashboard/activity", {
    params,
  });
};

export const getLawsRegulationsModuleData = (module: string, params = {}) => {
  return axiosInstance.get(`/api/laws-regulations-dashboard/data/${module}`, {
    params,
  });
};

export const getLawsRegulationsComplianceStatus = () => {
  return axiosInstance.get("/api/laws-regulations-dashboard/compliance");
};

// New consolidated dashboard endpoint
export const getLawsRegulationsConsolidatedData = (params = {}) => {
  return axiosInstance.get("/api/laws-regulations-dashboard/consolidated", {
    params,
  });
};

// Laws and Regulations Module API functions
export const getPoliciesByModule = (module: string, params = {}) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/policiesmanuals/module/${module}`,
    { params },
  );
};

export const createLawsRegulationsFolder = (param: {
  details: { folderName: string }[];
}) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/policiesmanuals/createfilefolder?policyType=laws_and_regulations",
    param,
  );
};

export const updateLawsRegulationsFolder = (
  params: { folderName: string },
  id: string | undefined,
) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/policiesmanuals/${id}?policyType=laws_and_regulations`,
    params,
  );
};

export const deleteLawsRegulationsFolder = (id: string | undefined) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/policiesmanuals/${id}?policyType=laws_and_regulations`,
  );
};

export const searchLawsRegulations = (params: {
  keyword?: string;
  folderId?: string;
  region?: string;
  premises?: string;
}) => {
  return axiosInstance.get<ApiRes<any>>("/api/policiesmanuals/search", {
    params,
  });
};

export const filterLawsRegulations = (params: {
  region?: string;
  premises?: string;
  expiryDate?: string;
  folderName?: string;
}) => {
  return axiosInstance.get<ApiRes<any>>("/api/policiesmanuals/filter", {
    params,
  });
};

export const downloadLawsRegulationsFolder = (folderId: string | undefined) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/policiesmanuals/${folderId}/download`,
  );
};

export const uploadCertificateToFolder = async (
  folderId: string,
  file: File,
  tags: string[],
  expiryDate: string,
  reminder: string,
  region: string,
  premises: string,
  certificateName: string,
  issueDate: string,
  issuedBy: string,
) => {
  // Since the backend S3 upload is failing, we'll need to handle this differently
  // For now, let's create a simple file upload that returns a temporary URL
  // In production, you should fix the backend S3 upload endpoint

  // Create a temporary file URL (this is just for testing - not persistent)
  const fileUrl = URL.createObjectURL(file);

  // Then update the folder with the new file
  const fileData = {
    files: [
      {
        name: file.name,
        size: file.size,
        tags: tags,
        expiry: expiryDate,
        reminder: reminder,
        url: fileUrl,
        type: file.type,
        region: region,
        premises: premises,
        certificateName: certificateName,
        issueDate: issueDate,
        issuedBy: issuedBy,
        expirationDate: expiryDate,
      },
    ],
  };

  return axiosInstance.put<ApiRes<any>>(
    `/api/policiesmanuals/${folderId}?policyType=laws_and_regulations`,
    fileData,
  );
};

export const getAllLawsRegulationsFolders = (params = {}) => {
  return axiosInstance.get<ApiRes<any>>(
    "/api/policiesmanuals?policyType=laws_and_regulations",
    { params },
  );
};

// Supplier API functions
export const createSupplier = (data: {
  name: string;
  phoneNumber: string;
  email: string;
  address: string;
}) => {
  return axiosInstance.post("/api/supplier", data);
};

export const getAllSuppliers = () => {
  return axiosInstance.get("/api/supplier");
};

export const getSupplierById = (id: string) => {
  return axiosInstance.get(`/api/supplier/${id}`);
};

export const updateSupplier = (
  id: string,
  data: {
    name: string;
    phoneNumber: string;
    email: string;
    address: string;
  },
) => {
  return axiosInstance.put(`/api/supplier/${id}`, data);
};

export const deleteSupplier = (id: string) => {
  return axiosInstance.delete(`/api/supplier/${id}`);
};

// Equipment Management History API functions
export const createEquipmentHistory = async (
  data: Partial<EquipmentHistory>,
): Promise<{ data: EquipmentHistoryResponse<EquipmentHistory> }> => {
  return axiosInstance.post(`/api/equipment-management/history`, data);
};

export const getAllEquipmentHistory = async (): Promise<{
  data: EquipmentHistoryResponse<EquipmentHistory[]>;
}> => {
  return axiosInstance.get(`/api/equipment-management/history/all`);
};

export const getEquipmentHistory = async (
  filters: Record<string, any> = {},
): Promise<{ data: EquipmentHistoryResponse<EquipmentHistory[]> }> => {
  return axiosInstance.get(`/api/equipment-management/history`, {
    params: filters,
  });
};

export const getEquipmentHistoryById = async (
  id: string,
): Promise<{ data: EquipmentHistoryResponse<EquipmentHistory> }> => {
  return axiosInstance.get(`/api/equipment-management/history/${id}`);
};

export const updateEquipmentHistory = async (
  id: string,
  data: Partial<EquipmentHistory>,
): Promise<{ data: EquipmentHistoryResponse<EquipmentHistory> }> => {
  return axiosInstance.put(`/api/equipment-management/history/${id}`, data);
};

export const deleteEquipmentHistory = async (
  id: string,
): Promise<{ data: EquipmentHistoryResponse<{ message: string }> }> => {
  return axiosInstance.delete(`/api/equipment-management/history/${id}`);
};

/* TENANT CREATION */
export const createTenant = (data: CreateTenantData) => {
  return axiosInstance.post<TenantResponse>("/api/tenant/create", data);
};

export const getAllTenants = () => {
  return axiosInstance.get<ApiResponse<any>>("/api/tenant/all-tenants");
};

export const deleteTenant = (id: string) => {
  return axiosInstance.delete<ApiResponse<{ message: string }>>(
    `/api/tenant/${id}`,
  );
};

/* ----------Washroom Type List----------------- */

export const createTypeList = (data: WashroomTypeListType) => {
  return axiosInstance.post<WashroomTypeListResponse>(
    "/api/washroom-type-list/create",
    data,
  );
};

export const getAllTypeList = () => {
  return axiosInstance.get<ApiRes<WashroomTypeListResponse>>(
    "/api/washroom-type-list/all",
  );
};

export const getTypeById = (id: WashroomTypeListType) => {
  return axiosInstance.get<WashroomTypeListResponse>(
    `/api/washroom-type-list/${id}`,
  );
};

export const updateTypeList = (id: string, data: WashroomTypeListType) => {
  return axiosInstance.put<WashroomTypeListResponse>(
    `/api/washroom-type-list/${id}`,
    data,
  );
};

export const deleteTypeList = (id: string) => {
  return axiosInstance.delete<WashroomTypeListResponse>(
    `/api/washroom-type-list/${id}`,
  );
};
// ============== External Emergency
//  =================================Hazard and Vulnerability Assessment

export const createHazardousVulnerability = (data: any) => {
  return axiosInstance.post<ApiRes<any>>("/api/hazardous-vulnerability", data);
};

export const getAllHazardousVulnerability = () => {
  return axiosInstance.get<ApiRes<any>>("/api/hazardous-vulnerability");
};

export const getHazardousVulnerabilityById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/hazardous-vulnerability/${id}`);
};

export const updateHazardousVulnerability = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/hazardous-vulnerability/${id}`,
    data,
  );
};
export const deleteHazardousVulnerability = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/hazardous-vulnerability/${id}`,
  );
};
// Emergency Response Plans API functions
export const createEmergencyResponsePlan = (data: {
  erpName: string;
  scenarioType: string;
  scenarioTypeOther?: string;
  steps: Array<{ stepDescription: string; order: number }>;
  assignedRoleId: string;
  responseTimeSLA: number;
}) => {
  return axiosInstance.post<ApiRes<any>>("/api/emergency-response-plans", data);
};

// Emergency Response Plans Endpoints
export const getAllEmergencyResponsePlans = (params = {}) => {
  return axiosInstance.get<ApiRes<any>>("/api/emergency-response-plans", {
    params,
  });
};

export const getEmergencyResponsePlanById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/emergency-response-plans/${id}`);
};

export const updateEmergencyResponsePlan = (
  id: string,
  data: {
    erpName: string;
    scenarioType: string;
    scenarioTypeOther?: string;
    steps: Array<{ stepDescription: string; order: number }>;
    assignedRoleId: string;
    responseTimeSLA: number;
  },
) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/emergency-response-plans/${id}`,
    data,
  );
};

export const deleteEmergencyResponsePlan = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/emergency-response-plans/${id}`,
  );
};

// ========== BUSINESS CONTINUITY & RECOVERY ENDPOINTS ==========

// Business Continuity Plan Endpoints
export const getAllBCPPlans = (params = {}) => {
  return axiosInstance.get<ApiRes<any>>("/api/bcp-plans", { params });
};

export const getBCPPlanById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/bcp-plans/${id}`);
};

export const createBCPPlan = (data: any) => {
  return axiosInstance.post<ApiRes<any>>("/api/bcp-plans", data);
};

export const updateBCPPlan = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(`/api/bcp-plans/${id}`, data);
};

export const deleteBCPPlan = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(`/api/bcp-plans/${id}`);
};

export const activateBCPPlan = (id: string, data: any) => {
  return axiosInstance.post<ApiRes<any>>(`/api/bcp-plans/${id}/activate`, data);
};

export const deactivateBCPPlan = (id: string) => {
  return axiosInstance.post<ApiRes<any>>(`/api/bcp-plans/${id}/deactivate`);
};

export const linkBCPToIncident = (id: string, data: any) => {
  return axiosInstance.post<ApiRes<any>>(
    `/api/bcp-plans/${id}/link-incident`,
    data,
  );
};

export const getBCPPlanByIncident = (incidentId: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/bcp-plans/incident/${incidentId}`,
  );
};

// Recovery Task Endpoints
export const getAllRecoveryTasks = (params = {}) => {
  return axiosInstance.get<ApiRes<any>>("/api/recovery-tasks", { params });
};

export const getRecoveryTaskById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/recovery-tasks/${id}`);
};

export const createRecoveryTask = (data: any) => {
  return axiosInstance.post<ApiRes<any>>("/api/recovery-tasks", data);
};

export const updateRecoveryTask = (id: string, data: any) => {
  return axiosInstance.put<ApiRes<any>>(`/api/recovery-tasks/${id}`, data);
};

export const deleteRecoveryTask = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(`/api/recovery-tasks/${id}`);
};

export const updateRecoveryTaskProgress = (id: string, progress: number) => {
  return axiosInstance.put<ApiRes<any>>(`/api/recovery-tasks/${id}/progress`, {
    progress,
  });
};

export const updateRecoveryTaskStatus = (id: string, status: string) => {
  return axiosInstance.put<ApiRes<any>>(`/api/recovery-tasks/${id}/status`, {
    status,
  });
};

export const getRecoveryTasksByBCP = (bcpPlanId: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/recovery-tasks/bcp/${bcpPlanId}`);
};

export const getRecoveryTimeline = (bcpPlanId: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/recovery-tasks/timeline/${bcpPlanId}`,
  );
};

export const getRecoveryStatistics = (bcpPlanId: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/recovery-tasks/statistics/${bcpPlanId}`,
  );
};
// ========== Utility Systems Endpoints ==========
export const createAssetInventory = (param: AssetInventory) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/asset-inventory-tracking",
    param,
  );
};
export const getAssetInventory = () => {
  return axiosInstance.get<ApiRes<any>>("/api/asset-inventory-tracking");
};
export const updateAssetsInventory = (id: string, param: AssetInventory) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/asset-inventory-tracking/${id}`,
    param,
  );
};
export const deleteAssetsInventory = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/asset-inventory-tracking/${id}`,
  );
};

// Space Utilization API functions
export const createSpaceUtilization = (param: SpaceUtilization) => {
  return axiosInstance.post<ApiRes<any>>("/api/space-utilization", param);
};

export const getSpaceUtilization = (params?: {
  premiseId?: string;
  projectId?: string;
  utilizationType?: "before" | "after";
  areaId?: string;
  isActive?: boolean;
  limit?: number;
  skip?: number;
  sort?: string;
}) => {
  return axiosInstance.get<ApiRes<any>>("/api/space-utilization", {
    params,
  });
};

export const getSpaceUtilizationById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/space-utilization/${id}`);
};

export const updateSpaceUtilization = (id: string, param: SpaceUtilization) => {
  return axiosInstance.put<ApiRes<any>>(`/api/space-utilization/${id}`, param);
};

export const deleteSpaceUtilization = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(`/api/space-utilization/${id}`);
};

export const getSpaceUtilizationByProject = (projectId: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/space-utilization/project/${projectId}`,
  );
};

export const getSpaceUtilizationByPremise = (premiseId: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/space-utilization/premise/${premiseId}`,
  );
};
export const getAssetsInventoryById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/asset-inventory-tracking/${id}`);
};

// Contractor & Vendor Management
export const createContractorVendor = (param: ContractorVendorManagement) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/contractor-vendor-management",
    param,
  );
};

export const getContractorVendors = () => {
  return axiosInstance.get<ApiRes<any>>("/api/contractor-vendor-management");
};

export const updateContractorVendor = (
  id: string,
  param: ContractorVendorManagement,
) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/contractor-vendor-management/${id}`,
    param,
  );
};

export const deleteContractorVendor = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/contractor-vendor-management/${id}`,
  );
};

export const getContractorVendorById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(
    `/api/contractor-vendor-management/${id}`,
  );
};

// ====== Mater data for Utility System Endpoints ==========
export const createUtilityAssetType = (data: UtilityAssetTypeListType) => {
  return axiosInstance.post<ApiRes<any>>(`/api/utility-asset-types`, data);
};
export const getAllUtilityAssetTypes = () => {
  return axiosInstance.get<ApiRes<any>>(`/api/utility-asset-types`);
};
export const updateUtilityAssetType = (
  id: string,
  data: UtilityAssetTypeListType,
) => {
  return axiosInstance.put<ApiRes<any>>(`/api/utility-asset-types/${id}`, data);
};
export const deleteUtilityAssetType = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(`/api/utility-asset-types/${id}`);
};
// ============================================
// Emergency Preparedness - Communication & Notification System
// ============================================

export const getNotifications = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/emergency-notifications", {
    params,
  });
};

export const getNotificationById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/emergency-notifications/${id}`);
};

export const sendEmergencyNotification = (data: any) => {
  return axiosInstance.post<ApiRes<any>>("/api/emergency-notifications", data);
};

export const acknowledgeNotification = (
  id: string,
  data: { status: string },
) => {
  return axiosInstance.put<ApiRes<any>>(
    `/api/emergency-notifications/${id}/acknowledge`,
    data,
  );
};

export const getUserNotifications = () => {
  return axiosInstance.get<ApiRes<any>>("/api/emergency-notifications");
};

// ============================================
// Emergency Preparedness - ERP Execution
// ============================================

export const triggerERPExecution = (data: any) => {
  return axiosInstance.post<ApiRes<any>>("/api/erp-executions/trigger", data);
};

export const getERPExecutions = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/erp-executions", { params });
};

export const getERPExecutionById = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/erp-executions/${id}`);
};

export const startExecutionStep = (executionId: string, stepNumber: number) => {
  return axiosInstance.post<ApiRes<any>>(
    `/api/erp-executions/${executionId}/steps/${stepNumber}/start`,
  );
};

export const completeExecutionStep = (
  executionId: string,
  stepNumber: number,
  notes?: string,
) => {
  return axiosInstance.post<ApiRes<any>>(
    `/api/erp-executions/${executionId}/steps/${stepNumber}/complete`,
    { notes },
  );
};

export const skipExecutionStep = (
  executionId: string,
  stepNumber: number,
  skipReason: string,
) => {
  return axiosInstance.post<ApiRes<any>>(
    `/api/erp-executions/${executionId}/steps/${stepNumber}/skip`,
    { skipReason },
  );
};

export const reportDeviation = (
  executionId: string,
  stepNumber: number,
  data: any,
) => {
  return axiosInstance.post<ApiRes<any>>(
    `/api/erp-executions/${executionId}/steps/${stepNumber}/deviation`,
    data,
  );
};

export const reportIssue = (
  executionId: string,
  stepNumber: number,
  data: any,
) => {
  return axiosInstance.post<ApiRes<any>>(
    `/api/erp-executions/${executionId}/steps/${stepNumber}/issue`,
    data,
  );
};

export const completeERPExecution = (id: string) => {
  return axiosInstance.put<ApiRes<any>>(`/api/erp-executions/${id}/complete`);
};

export const abortERPExecution = (id: string, abortReason: string) => {
  return axiosInstance.put<ApiRes<any>>(`/api/erp-executions/${id}/abort`, {
    abortReason,
  });
};

export const generatePostIncidentReport = (id: string) => {
  return axiosInstance.post<ApiRes<any>>(
    `/api/erp-executions/${id}/generate-report`,
  );
};

// ============================================
// Emergency Preparedness - Monitoring & Reporting
// ============================================

export const getRiskHeatmap = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/monitoring/risk-heatmap", {
    params,
  });
};

export const getDrillCompliance = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/monitoring/drill-compliance", {
    params,
  });
};

export const getERPEffectiveness = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/monitoring/erp-effectiveness", {
    params,
  });
};

export const getAuditLogs = (params?: any) => {
  return axiosInstance.get<ApiRes<any>>("/api/monitoring/audit-logs", {
    params,
  });
};

export const scheduleReport = (data: any) => {
  return axiosInstance.post<ApiRes<any>>(
    "/api/monitoring/schedule-report",
    data,
  );
};

export const getScheduledReports = () => {
  return axiosInstance.get<ApiRes<any>>("/api/monitoring/scheduled-reports");
};

// Compliance Endpoints
export const createComplianceCertificate = (
  param: Partial<ComplianceCertificate>,
) => {
  return axiosInstance.post<ApiRes<ComplianceCertificate>>(
    "/api/compliance/certificates",
    param,
  );
};

export const updateComplianceCertificate = (
  id: string,
  data: Partial<ComplianceCertificate>,
) => {
  return axiosInstance.put<ApiRes<ComplianceCertificate>>(
    `/api/compliance/certificates/${id}`,
    data,
  );
};

export const getComplianceCertificates = () => {
  return axiosInstance.get<ApiRes<ComplianceCertificate[]>>(
    "/api/compliance/certificates",
  );
};

export const deleteComplianceCertificate = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(
    `/api/compliance/certificates/${id}`,
  );
};

export const createComplianceChecklist = (
  param: Partial<ComplianceChecklist>,
) => {
  return axiosInstance.post<ApiRes<ComplianceChecklist>>(
    "/api/compliance/checklists",
    param,
  );
};

export const getComplianceChecklists = () => {
  return axiosInstance.get<ApiRes<ComplianceChecklist[]>>(
    "/api/compliance/checklists",
  );
};

export const updateComplianceChecklist = (
  id: string,
  param: Partial<ComplianceChecklist>,
) => {
  return axiosInstance.put<ApiRes<ComplianceChecklist>>(
    `/api/compliance/checklists/${id}`,
    param,
  );
};

export const deleteComplianceChecklist = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(`/api/compliance/checklists/${id}`);
};

export const generateComplianceChecklists = () => {
  return axiosInstance.post<ApiRes<ComplianceChecklist[]>>(
    "/api/compliance/checklists/generate",
  );
};
// Compliance Checklist Configs
export const createComplianceChecklistConfig = (
  param: Partial<ComplianceChecklistConfig>,
) => {
  return axiosInstance.post<ApiRes<ComplianceChecklistConfig>>(
    "/api/compliance/configs",
    param,
  );
};

export const getComplianceChecklistConfigs = () => {
  return axiosInstance.get<ApiRes<ComplianceChecklistConfig[]>>(
    "/api/compliance/configs",
  );
};

export const deleteComplianceChecklistConfig = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(`/api/compliance/configs/${id}`);
};
// ========== SAFETY & FACILITY ROUNDS ==========
// ==== Round Planning & Scheduling

export const createRoundPlanning = (data: roundPlanningType) => {
  return axiosInstance.post<ApiRes<any>>(`/api/round-planning`, data);
};
export const updateRoundPlanning = (data: roundPlanningType, id: String) => {
  return axiosInstance.put<ApiRes<any>>(`/api/round-planning/${id}`, data);
};
export const deleteRoundPlanning = (id: string) => {
  return axiosInstance.delete<ApiRes<any>>(`/api/round-planning/${id}`);
};
export const getAllRoundPlanning = () => {
  return axiosInstance.get<ApiRes<any>>(`/api/round-planning`);
};
export const getOneRoundPlanning = (id: string) => {
  return axiosInstance.get<ApiRes<any>>(`/api/round-planning/${id}`);
};
